import React, { useEffect, useState } from 'react';
import axios from '../axios';
import { useTranslation } from 'react-i18next';
import MobileDataCard from '../components/MobileDataCard';
import { formatDateTime } from '../utils/formatters';

interface AuditEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  metadataJson?: string;
  createdAt: string;
}

const AuditLog: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [entries, setEntries] = useState<AuditEntry[]>([]);

  const formatLabel = (value: string) =>
    value
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const actionLabel = (value: string) => t(`audit.actions.${value}`, formatLabel(value));
  const entityLabel = (value: string) => t(`audit.entities.${value}`, formatLabel(value));

  const fetchAudit = async () => {
    const response = await axios.get('/api/admin/audit');
    setEntries(response.data);
  };

  useEffect(() => {
    fetchAudit();
  }, []);

  return (
    <section className="mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('audit.title')}</h1>
      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div>
            <div className="text-sm font-semibold text-[#111D4E]">{t('audit.title')}</div>
            <div className="text-xs text-gray-600">{t('audit.subtitle')}</div>
          </div>
          <button onClick={fetchAudit} className="px-4 py-2 bg-[#47ABDB] text-white rounded-md text-sm font-semibold shadow-sm hover:bg-[#3d8fb8]">{t('common.refresh')}</button>
        </div>
        <div className="space-y-3 p-4 md:hidden">
          {entries.length === 0 ? (
            <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-10 text-center text-base text-gray-500">{t('audit.empty')}</div>
          ) : (
            entries.map((entry) => (
              <MobileDataCard
                key={entry.id}
                title={actionLabel(entry.action)}
                subtitle={entityLabel(entry.entityType)}
                rows={[
                  { label: t('audit.timestamp'), value: formatDateTime(entry.createdAt, i18n.resolvedLanguage) },
                ]}
              />
            ))
          )}
        </div>
        <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full text-left table-auto text-[15px] md:text-base">
          <thead className="bg-[#F3F6F9] text-sm uppercase tracking-wide text-[#111D4E]">
            <tr>
              <th className="px-5 py-3 font-semibold">{t('audit.action')}</th>
              <th className="px-5 py-3 font-semibold">{t('audit.entity')}</th>
              <th className="px-5 py-3 font-semibold">{t('audit.timestamp')}</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-5 py-10 text-center text-base text-gray-500">{t('audit.empty')}</td>
              </tr>
            ) : (
              entries.map((entry, idx) => (
                <tr key={entry.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FBFCFE]'}>
                  <td className="px-5 py-4 font-medium text-[#111D4E]">{actionLabel(entry.action)}</td>
                  <td className="px-5 py-4">{entityLabel(entry.entityType)}</td>
                  <td className="px-5 py-4">{formatDateTime(entry.createdAt, i18n.resolvedLanguage)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </section>
  );
};

export default AuditLog;
